import { ButtonProps, useButton } from '@mui/base';
import { prepareForSlot } from '@mui/base/utils';
import { AnchorHTMLAttributes, ReactNode, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { type VariantProps } from 'tailwind-variants';
import { cx } from '@/themes/utils/cssUtils';
import { CircularProgress } from '@mui/material';
import { button } from './variants';

const LinkSlot = prepareForSlot(Link);

export type ButtonVariants = VariantProps<typeof button>;

export interface MuiButtonProps
  extends ButtonVariants, Omit<ButtonProps, 'color'> {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  'data-testid'?: string;
  form?: string;
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
}

const MuiButton = forwardRef<HTMLButtonElement, MuiButtonProps>(
  function MuiButton(
    {
      children,
      startIcon,
      endIcon,
      loading,
      fullWidth,
      to,
      href,
      rel,
      target,
      className,
      'data-testid': dataTestId,
      form,
      ...props
    },
    ref,
  ) {
    const tv = button({
      variant: props.variant,
      size: props.size,
      color: props.color,
      theme: props.theme,
      loading,
      disabled: props.disabled,
      grouped: props.grouped,
      fullWidth,
    });

    const { getRootProps } = useButton({
      ...props,
      rootRef: ref,
    });

    const Comp = to ? LinkSlot : href ? 'a' : 'button';
    // A caller opening a link in a new tab must not leave the opened page
    // able to reach back into this one via window.opener.
    const resolvedRel =
      rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);
    const linkProps = to
      ? { to, rel: resolvedRel, role: 'link', target }
      : href
        ? { href, rel: resolvedRel, role: 'link', target }
        : undefined;

    return (
      <Comp
        {...getRootProps()}
        {...linkProps}
        style={props.style}
        className={cx(tv.base(), className)}
        data-testid={dataTestId}
        name={props.name}
        form={form}
      >
        {loading ? (
          <div className={tv.startIcon()}>
            <CircularProgress
              size={props.size === 'small' ? '0.875rem' : '1rem'}
              thickness={4}
              color="inherit"
            />
          </div>
        ) : (
          startIcon && <div className={tv.startIcon()}>{startIcon}</div>
        )}
        {children}
        {endIcon && <div className={tv.endIcon()}>{endIcon}</div>}
      </Comp>
    );
  },
);

/**
 * A wrapper to control and override the API of buttons.
 */
export { MuiButton };
